import { create } from 'zustand'
import { getJourney, type Journey } from '@/api/journey'

/**
 * The open project's journey (api/journey.ts), shared by the TopBar (its tab
 * set + status line), the Next step tab, the strip on every other tab and the
 * Go live checklist. One fetch, one answer — they can never disagree.
 */
interface JourneyState {
  project: string | null
  journey: Journey | null
  loading: boolean
  error: string | null
  /** Fetch (or re-fetch) the journey of `project`. Stale answers are dropped. */
  refresh: (project: string) => Promise<void>
  clear: () => void
}

let seq = 0

export const useJourneyStore = create<JourneyState>((set, get) => ({
  project: null,
  journey: null,
  loading: false,
  error: null,

  refresh: async (project: string) => {
    const mine = ++seq
    const switching = get().project !== project
    set({ project, loading: true, ...(switching ? { journey: null, error: null } : {}) })
    try {
      const journey = await getJourney(project)
      if (mine !== seq) return
      set({ journey, loading: false, error: null })
    } catch (err) {
      if (mine !== seq) return
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      console.error('[journey] could not load the next step', err)
      set({ loading: false, error: typeof detail === 'string' && detail ? detail : 'Could not work out the next step' })
    }
  },

  clear: () => {
    seq++
    set({ project: null, journey: null, loading: false, error: null })
  },
}))

/** Re-read the journey of the open project (after any write that moves it). */
export function refreshJourney(): void {
  const { project, refresh } = useJourneyStore.getState()
  if (project) void refresh(project)
}
